// src/components/jobs/workflows/WorkflowDefinitions.js
// Workflow definitions for temporal.io-style job organization
//
// This file defines ONLY the UI grouping of jobs into workflows.
// Job metadata (labels, descriptions, parameters) comes from the backend schema.
// See useJobSchemas hook for dynamic job type data.

import {
    MagnifyingGlassIcon,
    DocumentArrowDownIcon,
    DocumentTextIcon,
    SparklesIcon,
    CircleStackIcon,
    MapPinIcon,
    BellIcon,
    CogIcon,
    ArchiveBoxIcon
} from '@heroicons/react/24/outline';

/**
 * Workflow definitions grouping related job activities
 *
 * This is the SINGLE source of truth for workflow groupings.
 * Activities are job types in UPPER_CASE format.
 */
export const WORKFLOWS = {
    TenderDiscovery: {
        id: 'tender-discovery',
        label: 'Tender Discovery',
        description: 'Find and sync new tenders from external sources',
        icon: MagnifyingGlassIcon,
        color: 'blue',
        bgColor: 'bg-blue-900',
        textColor: 'text-blue-300',
        category: 'crawler',
        // backfill_* are source-data repair jobs (DÖE dedup keys, sbund detail
        // enrichment) that live alongside discovery in the engine.
        activities: ['CHECK_NEW', 'SYNC', 'GENERATE_TED_QUERY', 'BACKFILL_DEDUP_KEYS', 'BACKFILL_SBUND_DETAILS']
    },
    TenderCrawling: {
        id: 'tender-crawling',
        label: 'Tender Crawling',
        description: 'Download tender documents and details',
        icon: DocumentArrowDownIcon,
        color: 'green',
        bgColor: 'bg-green-900',
        textColor: 'text-green-300',
        category: 'crawler',
        activities: ['CRAWL', 'CRAWL_ALL', 'DEEP_CRAWL', 'DEEP_CRAWL_ALL', 'DEEP_CRAWL_BY_DOMAIN']
    },
    DocumentProcessing: {
        id: 'document-processing',
        label: 'Document Processing',
        description: 'Convert and extract text from tender documents',
        icon: DocumentTextIcon,
        color: 'yellow',
        bgColor: 'bg-yellow-900',
        textColor: 'text-yellow-300',
        category: 'documents',
        activities: ['REFRESH_DOCUMENTS', 'CONVERT_DOCUMENTS', 'CONVERT_ALL_DOCUMENTS', 'EXTRACT_DOCUMENT_TEXT', 'EXTRACT_TENDER_DOCUMENTS_TEXT', 'EXTRACT_ALL_DOCUMENTS_TEXT', 'NORMALIZE_DOCUMENTS', 'FILL_TENDER_FORMS']
    },
    TenderAssessment: {
        id: 'tender-assessment',
        label: 'Tender Assessment',
        description: 'AI-powered tender evaluation, narratives and facet analyses',
        icon: SparklesIcon,
        color: 'purple',
        bgColor: 'bg-purple-900',
        textColor: 'text-purple-300',
        category: 'assessment',
        activities: ['PREQUALIFY_TENDERS', 'ASSESS_TENDER', 'ASSESS_ALL_TENDERS', 'GENERATE_NARRATIVE_ASSESSMENT', 'GENERATE_NARRATIVES_BULK', 'GENERATE_FACET_ANALYSIS', 'GENERATE_FACET_ANALYSES_BULK']
    },
    SearchIndexing: {
        id: 'search-indexing',
        label: 'Search Indexing',
        description: 'Vector search index operations',
        icon: CircleStackIcon,
        color: 'cyan',
        bgColor: 'bg-cyan-900',
        textColor: 'text-cyan-300',
        category: 'index',
        activities: ['INDEX_TENDER', 'INDEX_ALL_TENDERS', 'SYNC_INDEX', 'SEARCH_CORPUS', 'RESCORE_CORPUS_V3', 'RECREATE_INDEX', 'REVERSE_RECREATE_INDEX', 'REVERSE_INDEX_ALL_FINGERPRINTS']
    },
    Downloads: {
        id: 'downloads',
        label: 'Downloads',
        description: 'Tender document bundles (ZIP exports) and retention',
        icon: ArchiveBoxIcon,
        color: 'orange',
        bgColor: 'bg-orange-900',
        textColor: 'text-orange-300',
        category: 'exports',
        activities: ['TENDER_DOWNLOAD', 'CLEANUP_EXPIRED_DOWNLOADS']
    },
    Enrichment: {
        id: 'enrichment',
        label: 'Enrichment',
        description: 'Location and metadata enrichment',
        icon: MapPinIcon,
        color: 'pink',
        bgColor: 'bg-pink-900',
        textColor: 'text-pink-300',
        category: 'enrichment',
        activities: ['GEOCODE', 'GEOCODE_ALL']
    },
    Notifications: {
        id: 'notifications',
        label: 'Notifications',
        description: 'Email and notification jobs',
        icon: BellIcon,
        color: 'indigo',
        bgColor: 'bg-indigo-900',
        textColor: 'text-indigo-300',
        category: 'other',
        activities: ['SEND_SAVED_SEARCH_REPORTS', 'SEND_EMAIL', 'SEND_RENEWAL_REMINDERS', 'SEND_TRIAL_REMINDERS']
    },
    WorkflowOrchestration: {
        id: 'workflow-orchestration',
        label: 'Workflow Orchestration',
        description: 'Run and schedule workflow pipelines',
        icon: CogIcon,
        color: 'violet',
        bgColor: 'bg-violet-900',
        textColor: 'text-violet-300',
        category: 'workflow',
        activities: ['RUN_WORKFLOW', 'RUN_COMPANY_WORKFLOWS']
    }
};

/**
 * Get workflow by job type (activity)
 * @param {string} activityType - Job type in UPPER_CASE format
 * @returns {Object|null} Workflow definition with key, or null if not found
 */
export const getWorkflowByActivity = (activityType) => {
    const normalized = activityType?.toUpperCase();
    for (const [key, workflow] of Object.entries(WORKFLOWS)) {
        if (workflow.activities.includes(normalized)) {
            return { key, ...workflow };
        }
    }
    return null;
};

/**
 * Get category for a job type from workflow definitions
 * @param {string} jobType - Job type in any case
 * @returns {string} Category name or 'other'
 */
export const getCategoryForJobType = (jobType) => {
    const workflow = getWorkflowByActivity(jobType);
    return workflow?.category || 'other';
};

/**
 * Get all workflow entries as array
 * @returns {Array} Array of workflow objects with keys
 */
export const getWorkflows = () => {
    return Object.entries(WORKFLOWS).map(([key, workflow]) => ({
        key,
        ...workflow
    }));
};

/**
 * Get all unique categories from workflows
 * @returns {Object} Map of category id to label
 */
export const getCategories = () => {
    const categories = new Map();
    Object.values(WORKFLOWS).forEach(w => {
        if (!categories.has(w.category)) {
            categories.set(w.category, {
                id: w.category,
                label: w.category.charAt(0).toUpperCase() + w.category.slice(1),
                color: w.color
            });
        }
    });
    return Object.fromEntries(categories);
};

/**
 * Convert job type to human-readable label
 * @param {string} jobType - Job type in UPPER_CASE format (e.g., "DEEP_CRAWL")
 * @returns {string} Human-readable label (e.g., "Deep Crawl")
 */
export const jobTypeToLabel = (jobType) => {
    if (!jobType) return '';
    return jobType
        .toLowerCase()
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export default WORKFLOWS;
